import React, { useState, useEffect } from 'react';
import { getAdminInstructorApplications, updateInstructorApplicationStatus, deleteInstructorApplication } from '../../services/api';
import { Users, Search, Mail, Phone, BookOpen, Clock, CheckCircle, Trash2, ShieldCheck, ChevronDown } from 'lucide-react';

const InstructorApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await getAdminInstructorApplications();
      if (res.success) {
        setApplications(res.data || []);
      }
    } catch (e) {
      console.error("Error fetching instructor applications:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appId, newStatus) => {
    setUpdatingId(appId);
    try {
      const res = await updateInstructorApplicationStatus(appId, newStatus);
      if (res.success) {
        setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
      }
    } catch (e) {
      console.error("Error updating status:", e);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm("Are you sure you want to delete this instructor application?")) return;
    try {
      const res = await deleteInstructorApplication(appId);
      if (res.success) {
        setApplications(prev => prev.filter(a => a.id !== appId));
      }
    } catch (e) {
      console.error("Error deleting application:", e);
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch =
      (app.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.topic || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.experience || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'All' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalApps = applications.length;
  const pendingCount = applications.filter(a => a.status === 'Pending').length;
  const approvedCount = applications.filter(a => a.status === 'Approved' || a.status === 'Contacted').length;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/50';
      case 'Contacted':
      case 'Reviewed':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800/50';
      case 'Rejected':
        return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800/50';
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-emerald-800 via-teal-900 to-emerald-900 rounded-2xl p-4 sm:p-6 text-white shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="text-emerald-300" size={24} />
            <h1 className="text-xl sm:text-2xl font-black">Instructor Applications</h1>
          </div>
          <p className="text-xs sm:text-sm text-emerald-100">
            Review and manage applicant submissions for "Teach on E-Learning Hub".
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 px-3.5 py-2 rounded-xl text-center shrink-0">
            <p className="text-[10px] text-emerald-200 uppercase font-bold">Total Applications</p>
            <p className="text-lg font-black text-white">{totalApps}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 px-3.5 py-2 rounded-xl text-center shrink-0">
            <p className="text-[10px] text-amber-200 uppercase font-bold">Pending Review</p>
            <p className="text-lg font-black text-amber-300">{pendingCount}</p>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3 w-full sm:w-auto flex-1">
          <Search className="text-gray-400 shrink-0" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search applicant name, email, phone number, course topic, or experience..."
            className="w-full text-xs sm:text-sm focus:outline-none text-gray-800 dark:text-gray-100 bg-transparent"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
          <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Filter Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none"
          >
            <option value="All">All Statuses ({totalApps})</option>
            <option value="Pending">Pending ({pendingCount})</option>
            <option value="Contacted">Contacted</option>
            <option value="Approved">Approved ({approvedCount})</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400 text-sm">Loading applications...</div>
        ) : filteredApplications.length === 0 ? (
          <div className="p-12 text-center text-gray-400 dark:text-gray-500 text-sm">No instructor applications found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 text-gray-500 dark:text-gray-400 uppercase text-[10px] font-bold tracking-wider">
                <tr>
                  <th className="py-3.5 px-4">Applied Date</th>
                  <th className="py-3.5 px-4">Applicant Contact</th>
                  <th className="py-3.5 px-4">Course Topic</th>
                  <th className="py-3.5 px-4">Experience</th>
                  <th className="py-3.5 px-4">Bio / Proposal</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="py-3.5 px-4 whitespace-nowrap text-gray-500 dark:text-gray-400 text-xs">
                      {app.created_at ? new Date(app.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                    </td>

                    <td className="py-3.5 px-4 space-y-0.5">
                      <p className="font-bold text-gray-900 dark:text-gray-100">{app.name}</p>
                      <a
                        href={`mailto:${app.email}?subject=E-Learning Hub Instructor Application - ${encodeURIComponent(app.topic)}`}
                        className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 font-medium"
                      >
                        <Mail size={12} /> {app.email}
                      </a>
                      {app.phone && (
                        <a
                          href={`tel:${app.phone}`}
                          className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-medium"
                        >
                          <Phone size={12} /> {app.phone}
                        </a>
                      )}
                    </td>

                    <td className="py-3.5 px-4 font-bold text-gray-800 dark:text-gray-200">
                      <div className="flex items-center gap-1.5">
                        <BookOpen size={14} className="text-emerald-600 shrink-0" />
                        <span>{app.topic}</span>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap text-gray-700 dark:text-gray-300 font-medium">
                      {app.experience}
                    </td>

                    <td className="py-3.5 px-4 max-w-xs text-gray-600 dark:text-gray-300 text-xs leading-normal">
                      <p className="line-clamp-2">{app.bio || 'No bio provided'}</p>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <select
                        value={app.status || 'Pending'}
                        disabled={updatingId === app.id}
                        onChange={(e) => handleStatusChange(app.id, e.target.value)}
                        className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none ${getStatusBadgeClass(app.status)}`}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Reviewed">Reviewed</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap text-right space-x-1.5">
                      {app.phone && (
                        <a
                          href={`tel:${app.phone}`}
                          title="Call Applicant"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        >
                          <Phone size={14} />
                        </a>
                      )}
                      <a
                        href={`mailto:${app.email}?subject=E-Learning Hub Instructor Application - ${encodeURIComponent(app.topic)}`}
                        title="Email Applicant"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                      >
                        <Mail size={14} />
                      </a>
                      <button
                        onClick={() => handleDelete(app.id)}
                        title="Delete Application"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorApplications;
